import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatObservedWeatherMonthPill } from '../../lib/observedWeather';

type SelectionState = 'none' | 'some' | 'all';

interface PeriodTreeNode {
  key: string;
  label: string;
  periods: string[];
  children?: PeriodTreeNode[];
}

interface HierarchicalPeriodPickerProps {
  label: string;
  options: string[];
  selected: string[];
  periodKind?: string;
  onToggle: (value: string, enabled: boolean) => void;
  formatOption?: (value: string) => string;
  emptyMessage?: string;
}

function monthLabel(month: number): string {
  return new Date(2000, month - 1, 1).toLocaleDateString(undefined, { month: 'short' });
}

function buildPeriodTree(periods: string[], periodKind?: string): PeriodTreeNode[] {
  const sorted = [...periods].sort();

  if (periodKind === 'yearly' || (sorted.length > 0 && sorted.every((p) => /^\d{4}$/.test(p)))) {
    return sorted.map((year) => ({
      key: year,
      label: year,
      periods: [year],
    }));
  }

  const years = new Map<number, Map<number, string[]>>();

  for (const period of sorted) {
    const d = new Date(`${period}T00:00:00`);
    if (Number.isNaN(d.getTime())) continue;
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    if (!years.has(year)) years.set(year, new Map());
    const months = years.get(year)!;
    if (!months.has(month)) months.set(month, []);
    months.get(month)!.push(period);
  }

  const isMonthly = periodKind === 'monthly';

  return Array.from(years.entries())
    .sort(([a], [b]) => a - b)
    .map(([year, months]) => {
      const monthNodes = Array.from(months.entries())
        .sort(([a], [b]) => a - b)
        .map(([month, monthPeriods]) => ({
          key: `${year}-${month}`,
          label: monthLabel(month),
          periods: monthPeriods,
          children: isMonthly
            ? undefined
            : monthPeriods.map((period) => ({
                key: period,
                label: formatObservedWeatherMonthPill(period),
                periods: [period],
              })),
        }));

      return {
        key: String(year),
        label: String(year),
        periods: monthNodes.flatMap((m) => m.periods),
        children: monthNodes,
      };
    });
}

function getSelectionState(periods: string[], selectedSet: Set<string>): SelectionState {
  if (periods.length === 0) return 'none';
  let count = 0;
  for (const period of periods) {
    if (selectedSet.has(period)) count += 1;
  }
  if (count === 0) return 'none';
  if (count === periods.length) return 'all';
  return 'some';
}

function TriStateCheckbox({
  state,
  onChange,
  ariaLabel,
}: {
  state: SelectionState;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'all'}
      onChange={(e) => onChange(e.target.checked)}
      aria-label={ariaLabel}
      className="rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-400"
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function PeriodTreeRow({
  node,
  depth,
  selectedSet,
  expandedKeys,
  onToggleExpand,
  onTogglePeriods,
  formatOption,
  periodKind,
}: {
  node: PeriodTreeNode;
  depth: number;
  selectedSet: Set<string>;
  expandedKeys: Set<string>;
  onToggleExpand: (key: string) => void;
  onTogglePeriods: (periods: string[], enabled: boolean) => void;
  formatOption: (value: string) => string;
  periodKind?: string;
}) {
  const hasChildren = Boolean(node.children?.length);
  const isExpanded = expandedKeys.has(node.key);
  const state = getSelectionState(node.periods, selectedSet);
  const isLeaf = !hasChildren || (periodKind === 'monthly' && depth === 1);

  if (isLeaf && node.periods.length === 1) {
    const period = node.periods[0];
    const checked = selectedSet.has(period);
    return (
      <label
        className="flex items-center gap-2 py-0.5 px-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer"
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onTogglePeriods([period], e.target.checked)}
          className="rounded border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-400"
        />
        <span className="text-[11px] text-gray-700 dark:text-gray-300">{formatOption(period)}</span>
      </label>
    );
  }

  return (
    <div>
      <div
        className="flex items-center gap-1 py-0.5 px-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800/60"
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggleExpand(node.key)}
            className="p-0.5 text-gray-500 dark:text-gray-400"
            aria-label={isExpanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
          >
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span className="w-4" aria-hidden />
        )}
        <TriStateCheckbox
          state={state}
          onChange={(checked) => onTogglePeriods(node.periods, checked)}
          ariaLabel={`Select ${node.label}`}
        />
        <button
          type="button"
          onClick={() => {
            if (hasChildren) onToggleExpand(node.key);
          }}
          className="text-[11px] font-medium text-gray-700 dark:text-gray-300"
        >
          {node.label}
        </button>
        {node.periods.length > 1 && (
          <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto pr-1">
            {node.periods.filter((p) => selectedSet.has(p)).length}/{node.periods.length}
          </span>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <PeriodTreeRow
              key={child.key}
              node={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              expandedKeys={expandedKeys}
              onToggleExpand={onToggleExpand}
              onTogglePeriods={onTogglePeriods}
              formatOption={formatOption}
              periodKind={periodKind}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Compact year → month → week tree with tri-state checkboxes. */
export function HierarchicalPeriodPicker({
  label,
  options,
  selected,
  periodKind,
  onToggle,
  formatOption = formatObservedWeatherMonthPill,
  emptyMessage = 'None available yet',
}: HierarchicalPeriodPickerProps) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const tree = useMemo(() => buildPeriodTree(options, periodKind), [options, periodKind]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => {
    const latestYear = tree[tree.length - 1]?.key;
    return latestYear ? new Set([latestYear]) : new Set();
  });

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const togglePeriods = (periods: string[], enabled: boolean) => {
    for (const period of periods) {
      const isSelected = selectedSet.has(period);
      if (enabled && !isSelected) onToggle(period, true);
      if (!enabled && isSelected) onToggle(period, false);
    }
  };

  return (
    <div className="mx-2 mb-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
        {label}
      </p>
      {options.length === 0 ? (
        <p className="text-[11px] text-gray-400 dark:text-gray-500">{emptyMessage}</p>
      ) : (
        <div className="max-h-40 overflow-y-auto rounded-md border border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/30 py-1">
          {tree.map((node) => (
            <PeriodTreeRow
              key={node.key}
              node={node}
              depth={0}
              selectedSet={selectedSet}
              expandedKeys={expandedKeys}
              onToggleExpand={toggleExpand}
              onTogglePeriods={togglePeriods}
              formatOption={formatOption}
              periodKind={periodKind}
            />
          ))}
        </div>
      )}
    </div>
  );
}
